import { awsPartitionFromUse } from "@p0security/integrations-aws-shared/gov-cloud";
import {
  accessAnalyzerName,
  iamAssessmentInstallCommands,
  iamAssessmentTerraform,
} from "@p0security/integrations-aws-shared/policy/iam-assessment";
import { arnInfo } from "@p0security/integrations-aws-shared/util";
import { Typography } from "@p0security/ui";

import { CommandDisplay } from "../../CommandDisplay.tsx";
import type { AwsInstaller } from "../types";

const toAnalyzerName = ({
  existingArn,
  defaultName,
}: {
  existingArn?: string;
  defaultName: string;
}): string => {
  if (!existingArn) {
    return defaultName;
  }

  try {
    return arnInfo(existingArn, true)?.name ?? defaultName;
  } catch {
    return defaultName;
  }
};

const iamAssessmentInstructions: AwsInstaller["components"]["iam-assessment"]["instructions"] =
  (context, id) => {
    const awsPartition = awsPartitionFromUse(
      context.config,
      id,
      "iam-assessment"
    );
    return {
      help: (
        <>
          To install P0 on this account, provision P0&apos;s access using{" "}
          <Typography.Link
            href={`https://${id}.signin.aws.amazon.com/console`}
            target="_blank"
            rel="noopener"
          >
            AWS Cloud Shell
          </Typography.Link>{" "}
          or Terraform:
        </>
      ),
      commands: {
        shell: [
          {
            command: iamAssessmentInstallCommands(
              context,
              {},
              {
                gcloudServiceAccountId:
                  context.config.base[id]?.serviceAccountId,
                awsAccountId: id,
                awsPartition,
              }
            ),
          },
        ],
        iac: [
          {
            command: iamAssessmentTerraform(
              context,
              {},
              context.config.base[id]?.serviceAccountId,
              awsPartition
            ),
          },
        ],
      },
    };
  };

export const awsIamAssessmentInstaller: AwsInstaller["components"]["iam-assessment"] =
  {
    instructions: iamAssessmentInstructions,
    items: {
      accessAnalyzerArn: {
        labeler: (context, id) => {
          const analyzerName = toAnalyzerName({
            existingArn:
              context.config["iam-assessment"]?.[id]?.accessAnalyzerArn,
            defaultName: accessAnalyzerName,
          });

          return (
            <>
              This is the ARN of the access analyzer you created during set-up.
              To get the ARN, run the following command:
              <CommandDisplay
                commands={`aws accessanalyzer get-analyzer \\
  --analyzer-name ${analyzerName}`}
              />
            </>
          );
        },
      },
    },
  };
